import React, { useState, useMemo } from "react";
import { formatINR } from "../../../shared/services/api";
import { Calculator, Percent, Clock, Wallet } from "lucide-react";

/**
 * v2.70 Phase 4 — EMICalculator
 * Standard EMI = P * r * (1+r)^n / ((1+r)^n - 1)
 * Where P = principal, r = monthly rate (annual/12/100), n = tenure in months.
 */
export default function EMICalculator({ defaultPrice = 0 }) {
  const [loan, setLoan] = useState(Math.max(0, Math.round(defaultPrice * 0.8)));
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, totalInterest, totalPayable } = useMemo(() => {
    const P = Number(loan) || 0;
    const r = (Number(rate) || 0) / 12 / 100;
    const n = (Number(years) || 0) * 12;
    if (P <= 0 || r <= 0 || n <= 0) {
      return { emi: 0, totalInterest: 0, totalPayable: 0 };
    }
    const pow = Math.pow(1 + r, n);
    const monthly = (P * r * pow) / (pow - 1);
    const total = monthly * n;
    return {
      emi: Math.round(monthly),
      totalInterest: Math.round(total - P),
      totalPayable: Math.round(total),
    };
  }, [loan, rate, years]);

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="emi-calculator">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
          <Calculator size={15} />
        </div>
        <div>
          <h3 className="font-heading font-bold text-base text-[#09090B]">EMI Calculator</h3>
          <p className="text-[11px] text-[#71717A]">Estimate your monthly home-loan payment</p>
        </div>
      </div>

      <div className="space-y-4">
        <Field label="Loan Amount (₹)" right={<span className="font-mono-data text-[#0055FF]">{formatINR(loan)}</span>}>
          <input
            type="range"
            min="100000"
            max="100000000"
            step="100000"
            value={loan}
            onChange={(e) => setLoan(Number(e.target.value))}
            className="w-full accent-[#0055FF]"
            data-testid="emi-loan-slider"
          />
          <input
            type="number"
            value={loan}
            onChange={(e) => setLoan(Number(e.target.value) || 0)}
            className="w-full h-9 px-3 text-[13px] border border-[#E4E4E7] rounded-sm bg-white focus:outline-none focus:border-[#0055FF] mt-1"
            data-testid="emi-loan-input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Interest Rate (% p.a.)" right={<Percent size={11} className="text-[#71717A]" />}>
            <input
              type="number"
              step="0.1"
              min="0"
              max="30"
              value={rate}
              onChange={(e) => setRate(Number(e.target.value) || 0)}
              className="w-full h-9 px-3 text-[13px] border border-[#E4E4E7] rounded-sm bg-white focus:outline-none focus:border-[#0055FF]"
              data-testid="emi-rate-input"
            />
          </Field>
          <Field label="Tenure (Years)" right={<Clock size={11} className="text-[#71717A]" />}>
            <input
              type="number"
              min="1"
              max="40"
              value={years}
              onChange={(e) => setYears(Number(e.target.value) || 0)}
              className="w-full h-9 px-3 text-[13px] border border-[#E4E4E7] rounded-sm bg-white focus:outline-none focus:border-[#0055FF]"
              data-testid="emi-years-input"
            />
          </Field>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2 pt-4 border-t border-[#E4E4E7]">
        <Stat label="Monthly EMI" value={formatINR(emi)} highlight testid="emi-result-monthly" />
        <Stat label="Total Interest" value={formatINR(totalInterest)} testid="emi-result-interest" />
        <Stat label="Total Payable" value={formatINR(totalPayable)} testid="emi-result-total" />
      </div>

      <div className="mt-3 inline-flex items-center gap-1.5 text-[10.5px] text-[#71717A]">
        <Wallet size={11} /> Indicative only. Banks may apply processing fees + insurance separately.
      </div>
    </div>
  );
}

const Field = ({ label, right, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B]">{label}</label>
      {right}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, highlight, testid }) => (
  <div className={`rounded-sm p-2.5 ${highlight ? "bg-[#0055FF] text-white" : "bg-[#FAFAFA] border border-[#E4E4E7]"}`} data-testid={testid}>
    <div className={`text-[9.5px] uppercase tracking-wider font-semibold ${highlight ? "text-white/70" : "text-[#52525B]"}`}>{label}</div>
    <div className={`font-heading font-bold text-base font-mono-data mt-0.5 ${highlight ? "text-white" : "text-[#09090B]"}`}>{value}</div>
  </div>
);
